/*!
 * TimbricaOrganSynth — reference-grade dual organ engine for the browser.
 *
 *   var organ = TimbricaOrganSynth.create({ type: 'hammond' });
 *   organ.init();                       // builds the Web Audio graph (idempotent)
 *   await organ.resume();               // MUST be awaited on the first user gesture
 *   organ.setDrawbars([8,8,8,0,0,0,0,0,0]);
 *   organ.noteOn(60, 1);  organ.noteOff(60);
 *
 * Two physically-distinct engines share one signal graph:
 *
 *   HAMMOND (tonewheel), in two models:
 *     console — B-3 / C-3: 9 drawbars per manual, foldback at the top of the generator,
 *               scanner vibrato/chorus V1–V3 / C1–C3, single-trigger percussion that
 *               borrows the 1' busbar.
 *     spinet  — L-100 series (L-100 … L-111, L-112, L-122): 9 upper / 7 lower drawbars,
 *               no foldback (harmonics above the top wheel are simply absent), phase-shift
 *               vibrato with SMALL / NORMAL / both + CHORUS, 2nd and 3rd harmonic
 *               percussion that may sound together, borrows the 1⅓' busbar, decays in
 *               ~2.5 s or <0.5 s, and a built-in spring reverb (REVERB I / II).
 *     Both models play from ONE shared bank of tempered tonewheels, exactly like the
 *     instrument: two keys that need the same pitch read the same wheel, so their common
 *     partials add in phase instead of beating. Plus key-click, tube overdrive and a
 *     LESLIE 122/147 rotary speaker (horn + drum rotors, each with Doppler pitch-mod +
 *     amplitude tremolo + stereo pan) whose speeds and spin-up/down inertia are measured
 *     values, not taste.
 *
 *   PIPE (church):  per-note PeriodicWave built from the active stops (by footage),
 *      a 16' sub rank, soft chiff attack, tremulant, optional pedal coupler, and a
 *      long cathedral convolution reverb.
 *
 * Sources for the numbers (so nobody "tunes them by ear" later):
 *   - Hammond L-100 Series Service Manual (archive.org, HammondL100SeriesServiceManualComplete):
 *     87 wheels, manuals use frequencies 18–91, no key contact for harmonics above the top
 *     wheel, vibrato oscillator ≈ 6.8 c/s through three phase-shift stages, percussion keyed
 *     through the 6th-harmonic busbar, decay ≈ 2½ s (tab up) / < ½ s (FAST DECAY).
 *   - Hammond technical information, vibrato equipment: scanner 412 rpm; the widest vibrato
 *     scans the whole line in ~1/14 s for ~1½ % frequency change, No. 2 scans about half
 *     of it, No. 1 about a third.
 *   - Leslie 147 (= 122, balanced input only) measured by John Fisher: horn 400 / 48 rpm,
 *     drum 342 / 40 rpm. Spin-up/down time constants: Herrera, Hanson & Abel, "Discrete
 *     Time Emulation of the Leslie Speaker", AES 127th Convention, 2009.
 *   - Spring reverb: dispersive allpass chain in a feedback loop (Välimäki, Parker & Abel,
 *     "Parametric spring reverberation effect", JAES 2010); main chirps below fC with the
 *     group delay peaking at fC.
 *   docs/why/organ.md keeps the reasoning and what is still unmeasured.
 *
 * Authenticity note: a real organ is NOT velocity-sensitive — a key is either down or
 * up. Velocity here only scales the Hammond key-click. Expression is via setVolume()
 * (the swell pedal). This is deliberate and correct, not a limitation.
 *
 * No external dependencies. Pure Web Audio. ~Stereo out.
 */
window.TimbricaOrganSynth = (function () {
    'use strict';

    /* ───────────────────────── helpers ───────────────────────── */
    function midiToFreq(midi) { return 440 * Math.pow(2, (midi - 69) / 12); }

    /* Hammond drawbar → harmonic ratio of the played fundamental (nominal).
       16'  5⅓'  8'   4'   2⅔'  2'   1⅗'  1⅓'  1'                                  */
    var DRAWBAR_RATIOS = [0.5, 1.5, 1, 2, 3, 4, 5, 6, 8];
    /* …but on the instrument every harmonic is another tonewheel of the tempered scale,
       not the pure ratio: 2⅔' is 19 semitones up (×2.9966), 1⅗' is 28 (×5.0397). Those
       are the pitches that actually sound — and the reason a chord's shared partials come
       off the very same wheel. */
    var DRAWBAR_SEMIS = [-12, 7, 0, 12, 19, 24, 28, 31, 36];

    /* The generator's top wheel (frequency 91) is F♯8 ≈ 5920 Hz; the bottom one (1) is
       C1 ≈ 32.7 Hz. The L-100 series omits frequencies 14–17 and wires its manuals to
       18–91, so a manual harmonic below F2 has no wheel on a spinet. */
    var WHEEL_TOP_MIDI = 114;
    var WHEEL_BOTTOM_MIDI = 24;
    var SPINET_MANUAL_BOTTOM_MIDI = 41;

    /* Kept for API compatibility: the console fold, in Hz. */
    var TONEWHEEL_MAX = 5924;
    function foldHarmonic(freq) {
        while (freq > TONEWHEEL_MAX) freq *= 0.5;
        return freq;
    }

    /* Which wheel (as a MIDI pitch) a key's drawbar reads, or null when none does.
       console: harmonics above the top wheel fold back an octave at a time (B-3 wiring);
       spinet:  they have no key contact at all (L-100 manual wiring chart) — dropped. */
    function wheelMidiFor(model, midi, i) {
        var m = midi + DRAWBAR_SEMIS[i];
        if (model === 'spinet') {
            if (m > WHEEL_TOP_MIDI) return null;
            /* keys below the real F-to-C compass: borrow the octave above rather than go silent */
            while (m < SPINET_MANUAL_BOTTOM_MIDI) m += 12;
            return m;
        }
        while (m > WHEEL_TOP_MIDI) m -= 12;
        while (m < WHEEL_BOTTOM_MIDI) m += 12;
        return m;
    }

    /* Drawbar setting 0..8 → linear amplitude, ~3 dB per step (authentic taper). */
    function drawbarAmp(setting) {
        if (setting <= 0) return 0;
        return Math.pow(10, (setting - 8) * 0.15); /* 8→1.0, 7→0.707, … 1→~0.089 */
    }

    /* ── Vibrato ──
       console: the scanner sweeps an LC delay line. The whole line (~1.07 ms: 1½ % in
       1/14 s) for V3, about half for V2, a third for V1 — a sine of these amplitudes gives
       the documented ~1½ % mean frequency change at V3.
       spinet:  three phase-shift stages whose corners a pulse-shaped 6.8 c/s drive sweeps
       upward. Sweep depths (cents) chosen so the mean change at A4 equals the console's
       degree for degree; above ~1 kHz the phase-shift vibrato gets shallower, which is its
       character. Small / Normal / both is the L-100's own three degrees. */
    var SCANNER_HZ = 6.87;
    var SCANNER_DEPTH = { 1: 0.000179, 2: 0.000268, 3: 0.000536 };
    var PHASE_VIB_HZ = 6.8;
    var PHASE_VIB_CORNERS = [800, 1600, 3200];
    var PHASE_VIB_SWEEP = { S: 606, N: 986, SN: 3111 };

    /* Console code ↔ spinet code, so a registration survives switching the model. */
    var VIB_TO_SPINET = { V1: 'S', V2: 'N', V3: 'SN', C1: 'CS', C2: 'CN', C3: 'CSN' };
    var VIB_TO_CONSOLE = { S: 'V1', N: 'V2', SN: 'V3', CS: 'C1', CN: 'C2', CSN: 'C3' };
    function vibratoFor(model, v) {
        if (!v || v === 'off') return 'off';
        if (model === 'spinet') return VIB_TO_SPINET[v] || (VIB_TO_CONSOLE[v] ? v : 'off');
        return VIB_TO_CONSOLE[v] || (VIB_TO_SPINET[v] ? v : 'off');
    }

    /* ── Leslie 122 / 147 ── rotor speeds in Hz and first-order time constants in s. */
    var LESLIE_SPEED = {
        chorale: { horn: 48 / 60, drum: 40 / 60 },
        tremolo: { horn: 400 / 60, drum: 342 / 60 },
        brake:   { horn: 0, drum: 0 }
    };
    var LESLIE_TAU = { horn: { up: 0.161, down: 0.321 }, drum: { up: 4.127, down: 1.371 } };

    /* ── Percussion ── decay = time to silence (−60 dB-ish). */
    var PERC_DECAY = {
        console: { fast: 0.16, slow: 0.42 },
        spinet:  { fast: 0.45, slow: 2.5 }
    };

    /* ── L-100 reverb tabs: REVERB I, REVERB II, both (bit mask 1 | 2). */
    var SPRING_WET = [0, 0.22, 0.34, 0.48];

    /* ── Pipe-organ stop spectra ──
       Each stop contributes a harmonic-amplitude profile (index = harmonic-1) that is
       summed into a PeriodicWave built on the 8' fundamental. Footage maps to harmonic:
       8'=h1, 4'=h2, 2⅔'=h3, 2'=h4, 1⅗'=h5, mixture≈h6+h8+h10. 16' is a sub-octave and
       is generated by a separate oscillator (cannot live in an 8'-fundamental wave). */
    var STOP_SPECTRA = {
        /* Principal / Diapason 8' — the core organ tone: strong fundamental, gently
           tapering odd+even harmonics. */
        principal8: [1.0, 0.50, 0.34, 0.22, 0.15, 0.10, 0.07, 0.05, 0.035, 0.025, 0.018, 0.012],
        /* Flute 8' — almost pure fundamental + a whisper of 2nd. */
        flute8:     [1.0, 0.12, 0.04, 0.015, 0.006],
        /* Flute 4' — an octave-up flute (energy at h2, h4). */
        flute4:     [0, 1.0, 0.10, 0.04, 0.012],
        /* Twelfth 2⅔' — the quint (h3). */
        twelfth:    [0, 0, 1.0, 0, 0.10, 0, 0.03],
        /* Fifteenth 2' — super-octave (h4). */
        fifteenth:  [0, 0, 0, 1.0, 0, 0.12, 0, 0.04],
        /* Mixture — a bright stack of high ranks giving the plenum its brilliance. */
        mixture:    [0, 0, 0, 0, 0.6, 0.5, 0.45, 0.5, 0.4, 0.45, 0.35, 0.4, 0.3, 0.3, 0.25, 0.25],
        /* Reed / Trumpet 8' — buzzy, rich odd-dominant harmonics (sawtooth-like). */
        reed8:      [1.0, 0.7, 0.55, 0.42, 0.36, 0.28, 0.24, 0.19, 0.16, 0.13, 0.11, 0.09, 0.08, 0.06, 0.05, 0.04]
    };

    /* ───────────────────────── factory ───────────────────────── */
    function create(opts) {
        opts = opts || {};
        var inst = {};

        inst.type = opts.type || 'hammond';
        inst.ctx = null;
        inst._extCtx = opts.context || null;   /* offline/test context injection */
        inst.initialized = false;

        /* state — Hammond */
        inst.model = (opts.model === 'spinet') ? 'spinet' : 'console';
        inst.drawbars = (opts.drawbars || [8, 8, 8, 0, 0, 0, 0, 0, 0]).slice();
        inst.bassDrawbars = [8, 0, 8, 0, 0, 0, 0, 0, 0]; /* lower-manual / bass-split voice */
        inst.splitMidi = null;                    /* null = no split; else notes < this use bass */
        inst.leslie = 'chorale';                  /* chorale | tremolo | brake */
        inst.vibrato = 'off';                     /* off | V1 V2 V3 | C1 C2 C3  (spinet: S N SN | CS CN CSN) */
        inst.percussion = { on: false, harm: 3, soft: true, fast: true }; /* harm 2 | 3 | 23 (spinet: both) */
        inst.click = 0.30;                        /* key-click amount 0..1 */
        inst.overdrive = 0;                       /* 0..1 */
        inst.springReverb = 0;                    /* spinet REVERB tabs: 0 | 1 (I) | 2 (II) | 3 (both) */

        /* state — Pipe */
        inst.stops = { bourdon16: false, principal8: true, flute8: false, flute4: false,
                       twelfth: false, fifteenth: false, mixture: false, reed8: false };
        inst.tremulant = false;
        inst.pedal = false;

        inst.reverbAmount = (inst.type === 'pipe') ? 0.55 : 0.18;

        /* runtime */
        inst.activeNotes = {};        /* midi → handle */
        inst.heldOrder = [];          /* midi[] in press order (percussion + pedal) */
        inst.sustainOn = false;
        inst.pendingReleases = {};
        inst._pipeWave = null;        /* cached PeriodicWave for current stops */
        inst._pipeWaveDirty = true;
        inst._wheels = {};            /* wheel MIDI → running sine (the shared tonewheel bank) */
        inst._irCache = {};

        /* ─────────────── init ─────────────── */
        inst.init = function () {
            if (inst.initialized || inst._audioFailed) return inst;
            var ctx;
            if (inst._extCtx) {
                ctx = inst.ctx = inst._extCtx;             /* offline render / test */
            } else {
                var Ctor = window.AudioContext || window.webkitAudioContext;
                if (!Ctor) { inst._audioFailed = true; return inst; }
                /* On a browser with no real Web Audio, timbrica-core installs a SafeAC
                   shim as window.AudioContext that THROWS (AudioContextUnavailable) and
                   toasts. Swallow it once so it never spams or breaks the page. */
                try {
                    ctx = inst.ctx = new Ctor({ latencyHint: 'interactive' });
                } catch (e) {
                    inst._audioFailed = true;
                    return inst;
                }
            }

            /* ── master / limiter / analyser / recorder ── */
            inst.master = ctx.createGain();
            inst.master.gain.value = 0.62;

            /* soft brick-wall limiter so a full 10-note cluster + drawbars + Leslie +
               reverb cannot clip (telemetry-driven: organ users pile on chords). */
            var lim = ctx.createDynamicsCompressor();
            lim.threshold.value = -2.0;
            lim.knee.value = 0;
            lim.ratio.value = 20;
            lim.attack.value = 0.003;
            lim.release.value = 0.10;
            inst.limiter = lim;

            inst.analyser = ctx.createAnalyser();
            inst.analyser.fftSize = 2048;
            inst.analyser.smoothingTimeConstant = 0.78;

            inst.master.connect(lim);
            lim.connect(inst.analyser);
            inst.analyser.connect(ctx.destination);

            /* recorder tap (post-limiter, so the WAV matches what you hear) */
            try {
                inst.recDest = ctx.createMediaStreamDestination();
                inst.analyser.connect(inst.recDest);
            } catch (e) { inst.recDest = null; }

            /* ── reverb send (shared by both engines) ── */
            inst.reverbSend = ctx.createGain();
            inst.reverbConv = ctx.createConvolver();
            inst.reverbWet = ctx.createGain();
            inst.reverbSend.connect(inst.reverbConv);
            inst.reverbConv.connect(inst.reverbWet);
            inst.reverbWet.connect(inst.master);
            inst.reverbWet.gain.value = 0;

            _buildHammond(ctx, inst);
            _buildPipe(ctx, inst);
            _applyReverb(inst, true);

            inst.initialized = true;
            return inst;
        };

        /* Resume the context. Returns a Promise — AWAIT IT on the first user gesture,
           otherwise the first note is silent on strict-autoplay browsers (Safari/
           Yandex/mobile). This is the #1 cause of "I click and nothing happens". */
        inst.resume = function () {
            inst.init();
            if (!inst.ctx || inst._audioFailed) return Promise.resolve(false);
            if (inst.ctx.state === 'running') return Promise.resolve(true);
            try {
                return inst.ctx.resume().then(function () {
                    return inst.ctx.state === 'running';
                }).catch(function () { return false; });
            } catch (e) { return Promise.resolve(false); }
        };
        inst.isReady = function () { return !!inst.ctx && inst.ctx.state === 'running'; };
        inst.audioFailed = function () { return !!inst._audioFailed; };

        /* ─────────────── note on ─────────────── */
        inst.noteOn = function (midi, velocity) {
            inst.init();
            if (!inst.ctx) return null;
            if (typeof velocity !== 'number') velocity = 1;
            if (velocity < 0.05) velocity = 0.05; if (velocity > 1) velocity = 1;

            if (inst.activeNotes[midi]) inst._hardKill(midi); /* re-trigger */

            var useBass = (inst.splitMidi != null && midi < inst.splitMidi);
            /* Percussion is single-trigger on the upper (solo) manual only: it re-arms when
               every upper key is up, so a lower-manual chord never blocks it. */
            var firstOfPhrase = !useBass && !_upperHeld(inst);
            var handle = (inst.type === 'pipe')
                ? _pipeNoteOn(inst, midi)
                : _hammondNoteOn(inst, midi, velocity, firstOfPhrase, useBass);
            if (!handle) return null;
            inst.activeNotes[midi] = handle;
            inst.heldOrder.push(midi);

            /* Pedal coupler: the lowest currently-held note gets a 16' sub-octave. */
            if (inst.pedal) _refreshPedal(inst);
            return handle;
        };

        /* ─────────────── note off (with sustain) ─────────────── */
        inst.noteOff = function (midi) {
            if (!inst.ctx || !inst.activeNotes[midi]) return;
            if (inst.sustainOn) { inst.pendingReleases[midi] = true; return; }
            _release(inst, midi);
        };

        inst.setSustain = function (on) {
            inst.sustainOn = !!on;
            if (!on) {
                for (var m in inst.pendingReleases) {
                    if (inst.pendingReleases.hasOwnProperty(m)) _release(inst, parseInt(m, 10));
                }
                inst.pendingReleases = {};
            }
        };

        inst.stopAll = function () {
            for (var m in inst.activeNotes) {
                if (inst.activeNotes.hasOwnProperty(m)) inst._hardKill(parseInt(m, 10));
            }
            inst.activeNotes = {}; inst.heldOrder = []; inst.pendingReleases = {};
            if (inst._pedalHandle) { _killHandle(inst, inst._pedalHandle); inst._pedalHandle = null; }
        };
        inst.panic = inst.stopAll;

        inst._hardKill = function (midi) {
            var h = inst.activeNotes[midi];
            if (!h) return;
            _killHandle(inst, h);
            delete inst.activeNotes[midi];
            var i = inst.heldOrder.indexOf(midi);
            if (i >= 0) inst.heldOrder.splice(i, 1);
        };

        /* ─────────────── setters ─────────────── */
        inst.setType = function (t) {
            if (t === inst.type) return;
            inst.stopAll();
            inst.type = t;
            inst.reverbAmount = (t === 'pipe') ? 0.55 : 0.18;
            if (inst.initialized) _applyReverb(inst);
        };

        /* console (B-3 / C-3) | spinet (L-100 series: L-100 … L-122) */
        inst.setModel = function (m) {
            m = (m === 'spinet') ? 'spinet' : 'console';
            if (m === inst.model) return;
            var held = inst.heldOrder.slice();
            inst.stopAll();
            inst.model = m;
            inst.vibrato = vibratoFor(m, inst.vibrato);
            if (m === 'console' && inst.percussion.harm === 23) inst.percussion.harm = 3;
            if (inst.initialized) { _applyVibrato(inst); _applyReverb(inst); }
            for (var i = 0; i < held.length; i++) inst.noteOn(held[i], 0.8);
        };
        inst.getModel = function () { return inst.model; };

        /* Re-voice held notes in one zone (live drawbar pull, percussion cancel). A drawbar
           pulled out from 0 while the key is held gets wired to its wheel right here. */
        function _revoice(bassOnly) {
            if (!inst.ctx) return;
            var now = inst.ctx.currentTime;
            for (var m in inst.activeNotes) {
                if (!inst.activeNotes.hasOwnProperty(m)) continue;
                var h = inst.activeNotes[m];
                if (!h || !h.drawGains) continue;
                if (!!h.isBass !== !!bassOnly) continue;  /* only re-voice notes in this zone */
                for (var i = 0; i < 9; i++) {
                    if (!h.drawGains[i]) continue;
                    var amp = _drawbarLevel(inst, i, !!h.isBass) * h.noteScale;
                    if (amp > 0.00001) _wireDrawbar(inst, h, i);
                    h.drawGains[i].gain.setTargetAtTime(amp, now, 0.02);
                }
            }
        }
        inst.setDrawbars = function (arr) {
            if (!arr || arr.length !== 9) return;
            inst.drawbars = arr.slice();
            _revoice(false);
        };
        inst.setBassDrawbars = function (arr) {
            if (!arr || arr.length !== 9) return;
            inst.bassDrawbars = arr.slice();
            _revoice(true);
        };
        inst.setSplit = function (midi) {
            inst.splitMidi = (typeof midi === 'number') ? midi : null;
            /* re-trigger held notes so they land in the right zone */
            var held = inst.heldOrder.slice();
            inst.stopAll();
            for (var i = 0; i < held.length; i++) inst.noteOn(held[i], 0.8);
        };

        inst.setLeslie = function (mode) {
            inst.leslie = mode;
            if (inst.initialized) _applyLeslie(inst);
        };

        inst.setVibrato = function (v) {
            inst.vibrato = vibratoFor(inst.model, v);
            if (inst.initialized) _applyVibrato(inst);
        };

        inst.setPercussion = function (cfg) {
            inst.percussion = Object.assign({}, inst.percussion, cfg || {});
            if (inst.model === 'console' && inst.percussion.harm === 23) inst.percussion.harm = 3;
            _revoice(false);   /* the borrowed busbar's drawbar goes silent / comes back */
        };
        /* Which upper drawbar the percussion takes for its keying, if it is on. */
        inst.percussionCancels = function () {
            if (!inst.percussion.on) return -1;
            return inst.model === 'spinet' ? 7 : 8;   /* 1⅓' on the L-100, 1' on the B-3 */
        };
        inst.setClick = function (v) { inst.click = Math.max(0, Math.min(1, v)); };

        inst.setOverdrive = function (v) {
            inst.overdrive = Math.max(0, Math.min(1, v));
            if (inst.initialized) _applyOverdrive(inst);
        };

        inst.setSpringReverb = function (level) {
            inst.springReverb = Math.max(0, Math.min(3, level | 0));
            if (inst.initialized) _applyReverb(inst);
        };

        inst.setStops = function (stops) {
            inst.stops = Object.assign({}, inst.stops, stops || {});
            inst._pipeWaveDirty = true;
        };
        inst.setStop = function (name, on) {
            inst.stops[name] = !!on; inst._pipeWaveDirty = true;
        };

        inst.setTremulant = function (on) {
            inst.tremulant = !!on;
            if (inst.initialized) _applyTremulant(inst);
        };

        inst.setPedal = function (on) {
            inst.pedal = !!on;
            if (!inst.ctx) return;
            if (!on && inst._pedalHandle) { _killHandle(inst, inst._pedalHandle); inst._pedalHandle = null; }
            else if (on) _refreshPedal(inst);
        };

        inst.setReverb = function (amount) {
            inst.reverbAmount = Math.max(0, Math.min(1, amount));
            if (inst.reverbWet && inst.type === 'pipe') inst.reverbWet.gain.setTargetAtTime(inst.reverbAmount, inst.ctx.currentTime, 0.05);
        };

        inst.setVolume = function (v) {
            if (!inst.master) return;
            v = Math.max(0, Math.min(1, v));
            inst.master.gain.setTargetAtTime(v * 0.62, inst.ctx.currentTime, 0.02);
        };

        inst.getAnalyser = function () { return inst.analyser || null; };
        inst.getRecorderStream = function () { return inst.recDest ? inst.recDest.stream : null; };

        return inst;
    }

    /* Any upper-manual (non-bass) key currently down? */
    function _upperHeld(inst) {
        for (var m in inst.activeNotes) {
            if (inst.activeNotes.hasOwnProperty(m) && inst.activeNotes[m] && !inst.activeNotes[m].isBass) return true;
        }
        return false;
    }

    /* Level a drawbar contributes right now, after the instrument's own rules: the
       percussion keying busbar is not available, and the L-100's lower manual has no
       16' or 5⅓' drawbar. */
    function _drawbarLevel(inst, i, isBass) {
        if (isBass) {
            if (inst.model === 'spinet' && (i === 0 || i === 1)) return 0;
            return drawbarAmp(inst.bassDrawbars[i]);
        }
        if (i === inst.percussionCancels()) return 0;
        return drawbarAmp(inst.drawbars[i]);
    }

    /* ════════════════════ shared tonewheel bank ════════════════════ */
    /* One running sine per wheel, created the first time any key needs it. Keys connect
       to it through their own drawbar gain, the way key contacts close onto a busbar. */
    function _wheel(inst, wheelMidi) {
        var w = inst._wheels[wheelMidi];
        if (w) return w;
        var ctx = inst.ctx;
        w = ctx.createOscillator();
        w.type = 'sine';
        w.frequency.value = midiToFreq(wheelMidi);
        w.start();
        inst._wheels[wheelMidi] = w;
        return w;
    }

    function _wireDrawbar(inst, h, i) {
        if (h.wired[i] || h.wheelMidis[i] == null) return;
        _wheel(inst, h.wheelMidis[i]).connect(h.drawGains[i]);
        h.drawGains[i].connect(h.noteGain);
        h.wired[i] = true;
    }

    /* ════════════════════ Hammond graph ════════════════════ */
    function _buildHammond(ctx, inst) {
        inst.hammondBus = ctx.createGain();
        inst.hammondBus.gain.value = 1.0;

        var scanOut = ctx.createGain();

        /* ── console: scanner vibrato / chorus (modulated delay line) ── */
        inst.scanDry = ctx.createGain();
        inst.scanWet = ctx.createGain();
        inst.scanDelay = ctx.createDelay(0.02);
        inst.scanDelay.delayTime.value = 0.0008;
        inst.scanLfo = ctx.createOscillator();
        inst.scanLfo.type = 'sine';
        inst.scanLfo.frequency.value = SCANNER_HZ;
        inst.scanDepth = ctx.createGain();
        inst.scanDepth.gain.value = 0;                 /* set by _applyVibrato */
        inst.scanLfo.connect(inst.scanDepth);
        inst.scanDepth.connect(inst.scanDelay.delayTime);
        inst.scanLfo.start();

        inst.hammondBus.connect(inst.scanDry); inst.scanDry.connect(scanOut);
        inst.hammondBus.connect(inst.scanDelay); inst.scanDelay.connect(inst.scanWet); inst.scanWet.connect(scanOut);
        inst.scanDry.gain.value = 1; inst.scanWet.gain.value = 0;

        /* ── spinet: phase-shift vibrato (three swept allpass stages) ── */
        inst.psWet = ctx.createGain(); inst.psWet.gain.value = 0;
        inst.psLfo = ctx.createOscillator();
        inst.psLfo.type = 'sine';
        inst.psLfo.frequency.value = PHASE_VIB_HZ;
        /* The driver tube conducts on the positive half of the oscillator: the reactor
           current rises in pulses, the corners sweep up and come back, then rest. */
        inst.psPulse = ctx.createWaveShaper();
        inst.psPulse.curve = _pulseCurve();
        inst.psDepth = ctx.createGain(); inst.psDepth.gain.value = 0;   /* cents, by _applyVibrato */
        inst.psLfo.connect(inst.psPulse); inst.psPulse.connect(inst.psDepth);
        inst.psLfo.start();
        var psNode = null;
        inst.psStages = [];
        for (var s = 0; s < PHASE_VIB_CORNERS.length; s++) {
            var ap = ctx.createBiquadFilter();
            ap.type = 'allpass';
            ap.frequency.value = PHASE_VIB_CORNERS[s];
            ap.Q.value = 0.5;
            inst.psDepth.connect(ap.detune);
            if (psNode) psNode.connect(ap);
            psNode = ap;
            inst.psStages.push(ap);
        }
        /* the chain gets the organ's signal only while the spinet vibrato is on — three
           filters recomputed every sample are not free (measured: +30 % render cost on a
           chord when they ran all the time) */
        psNode.connect(inst.psWet); inst.psWet.connect(scanOut);

        /* ── tube overdrive ── */
        inst.odPre = ctx.createGain(); inst.odPre.gain.value = 1;
        inst.odShaper = ctx.createWaveShaper();
        inst.odShaper.curve = _softClipCurve(0);
        inst.odShaper.oversample = '2x';
        inst.odPost = ctx.createGain(); inst.odPost.gain.value = 1;
        scanOut.connect(inst.odPre); inst.odPre.connect(inst.odShaper); inst.odShaper.connect(inst.odPost);

        /* ── Leslie rotary speaker ── 800 Hz crossover, 12 dB/oct (122/147) */
        var crossHz = 800;
        inst.leslieIn = inst.odPost;

        /* horn (treble) rotor */
        var hornHP = ctx.createBiquadFilter(); hornHP.type = 'highpass'; hornHP.frequency.value = crossHz;
        /* drum (bass) rotor */
        var drumLP = ctx.createBiquadFilter(); drumLP.type = 'lowpass'; drumLP.frequency.value = crossHz;
        inst.leslieIn.connect(hornHP);
        inst.leslieIn.connect(drumLP);

        inst.horn = _buildRotor(ctx, hornHP, { baseDelay: 0.0022, dopDepth: 0.0010, amDepth: 0.30, panDepth: 0.85, hz: LESLIE_SPEED.chorale.horn });
        inst.drum = _buildRotor(ctx, drumLP, { baseDelay: 0.0040, dopDepth: 0.0006, amDepth: 0.18, panDepth: 0.55, hz: LESLIE_SPEED.chorale.drum });
        inst.horn.out.connect(inst.master);
        inst.drum.out.connect(inst.master);
        /* light dry blend so the cabinet keeps presence even when stopped */
        inst.leslieDry = ctx.createGain(); inst.leslieDry.gain.value = 0.12;
        inst.odPost.connect(inst.leslieDry); inst.leslieDry.connect(inst.master);

        /* hammond also feeds the reverb send */
        inst.odPost.connect(inst.reverbSend);

        _applyLeslie(inst);
        _applyVibrato(inst);
        _applyOverdrive(inst);
    }

    /* One Leslie rotor = modulated delay (Doppler) → tremolo gain (AM) → pan. A single
       LFO per rotor drives all three; horn and drum run at different rates and start at
       different phases so the stereo image churns the way a real cabinet does. */
    function _buildRotor(ctx, input, cfg) {
        var lfo = ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = cfg.hz;
        lfo.start();

        var delay = ctx.createDelay(0.05); delay.delayTime.value = cfg.baseDelay;
        var dopDepth = ctx.createGain(); dopDepth.gain.value = cfg.dopDepth;
        lfo.connect(dopDepth); dopDepth.connect(delay.delayTime);

        var trem = ctx.createGain(); trem.gain.value = 1.0;
        var amDepth = ctx.createGain(); amDepth.gain.value = 0; /* set by _applyLeslie */
        lfo.connect(amDepth); amDepth.connect(trem.gain);

        var pan = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
        var panDepth = ctx.createGain(); panDepth.gain.value = 0;
        if (pan) { lfo.connect(panDepth); panDepth.connect(pan.pan); }

        var out = ctx.createGain(); out.gain.value = 0.5;
        input.connect(delay); delay.connect(trem);
        if (pan) { trem.connect(pan); pan.connect(out); } else { trem.connect(out); }

        return { lfo: lfo, delay: delay, trem: trem, amDepth: amDepth, panDepth: panDepth,
                 out: out, cfg: cfg };
    }

    /* Apply the Leslie mode with the measured spin-up / spin-down inertia. The light horn
       is at speed in well under a second; the heavy drum takes ~4 s to wind up and ~1.4 s
       to slow — that lag between them is the signature. The constant depends on whether a
       rotor is speeding up or slowing down, not on which button was pressed. */
    function _applyLeslie(inst) {
        if (!inst.horn) return;
        var ctx = inst.ctx, now = ctx.currentTime;
        var target = LESLIE_SPEED[inst.leslie] || LESLIE_SPEED.chorale;
        ['horn', 'drum'].forEach(function (part) {
            var f = inst[part].lfo.frequency;
            var cur = f.value;
            var tau = (target[part] > cur) ? LESLIE_TAU[part].up : LESLIE_TAU[part].down;
            if (f.cancelAndHoldAtTime) f.cancelAndHoldAtTime(now);
            else { f.cancelScheduledValues(now); f.setValueAtTime(cur, now); }
            f.setTargetAtTime(target[part], now, tau);
        });

        /* depths: full when spinning, near-zero on brake */
        var k = (inst.leslie === 'brake') ? 0.04 : 1.0;
        inst.horn.amDepth.gain.setTargetAtTime(inst.horn.cfg.amDepth * k, now, 0.3);
        inst.drum.amDepth.gain.setTargetAtTime(inst.drum.cfg.amDepth * k, now, 0.3);
        inst.horn.panDepth.gain.setTargetAtTime(inst.horn.cfg.panDepth * k, now, 0.3);
        inst.drum.panDepth.gain.setTargetAtTime(inst.drum.cfg.panDepth * k, now, 0.3);
        inst.horn.delay.delayTime.cancelScheduledValues(now);
        inst.horn.delay.delayTime.setTargetAtTime(inst.horn.cfg.baseDelay, now, 0.1);
    }

    /* Feed the phase-shift chain only while it is heard; unhook it after its fade-out. */
    function _phaseChain(inst, on) {
        if (!!inst._psOn === on) return;
        inst._psOn = on;
        clearTimeout(inst._psOff);
        if (on) { try { inst.hammondBus.connect(inst.psStages[0]); } catch (e) {} return; }
        inst._psOff = setTimeout(function () {
            if (!inst._psOn) { try { inst.hammondBus.disconnect(inst.psStages[0]); } catch (e) {} }
        }, 200);
    }

    function _applyVibrato(inst) {
        if (!inst.scanDry) return;
        var now = inst.ctx.currentTime, v = inst.vibrato;
        function set(p, x) { p.setTargetAtTime(x, now, 0.02); }
        if (inst.model === 'spinet') {
            set(inst.scanWet.gain, 0); set(inst.scanDepth.gain, 0);
            var sweep = (v && v !== 'off') ? PHASE_VIB_SWEEP[v.replace(/^C/, '')] : 0;
            _phaseChain(inst, !!sweep);
            if (!sweep) { set(inst.scanDry.gain, 1); set(inst.psWet.gain, 0); set(inst.psDepth.gain, 0); return; }
            var chorus = v.charAt(0) === 'C';
            set(inst.psDepth.gain, sweep);
            set(inst.psWet.gain, chorus ? 0.7 : 1.0);
            set(inst.scanDry.gain, chorus ? 0.7 : 0.0);
            return;
        }
        set(inst.psWet.gain, 0); set(inst.psDepth.gain, 0);
        _phaseChain(inst, false);
        var deg = (v && v !== 'off') ? SCANNER_DEPTH[v.charAt(1)] : 0;
        if (!deg) { set(inst.scanDry.gain, 1); set(inst.scanWet.gain, 0); set(inst.scanDepth.gain, 0); return; }
        var isChorus = v.charAt(0) === 'C';
        set(inst.scanDepth.gain, deg);
        set(inst.scanWet.gain, isChorus ? 0.7 : 1.0);
        set(inst.scanDry.gain, isChorus ? 0.7 : 0.0);
    }

    function _applyOverdrive(inst) {
        if (!inst.odShaper) return;
        var amt = inst.overdrive, now = inst.ctx.currentTime;
        inst.odShaper.curve = _softClipCurve(amt);
        inst.odPre.gain.setTargetAtTime(1 + amt * 3.5, now, 0.05);
        inst.odPost.gain.setTargetAtTime(1 / (1 + amt * 1.6), now, 0.05);
    }

    /* Reverb follows the instrument: the cathedral for pipes, a modest room for the
       console, and the L-100's own spring tank (off unless a REVERB tab is down). */
    function _applyReverb(inst, immediate) {
        if (!inst.reverbConv) return;
        var kind, wet;
        if (inst.type === 'pipe') { kind = 'cathedral'; wet = inst.reverbAmount; }
        else if (inst.model === 'spinet') { kind = 'spring'; wet = SPRING_WET[inst.springReverb] || 0; }
        else { kind = 'room'; wet = 0.18; }
        if (inst._irKind !== kind) {
            var ir = inst._irCache[kind];
            if (!ir) {
                try {
                    ir = (kind === 'spring') ? _genSpringIR(inst.ctx)
                       : (kind === 'cathedral') ? _genIR(inst.ctx, 4.6, 2.4) : _genIR(inst.ctx, 1.6, 3.0);
                    inst._irCache[kind] = ir;
                } catch (e) { ir = null; }
            }
            if (ir) { try { inst.reverbConv.buffer = ir; inst._irKind = kind; } catch (e) {} }
        }
        if (immediate) inst.reverbWet.gain.value = wet;
        else inst.reverbWet.gain.setTargetAtTime(wet, inst.ctx.currentTime, 0.05);
    }

    /* ════════════════════ Pipe graph ════════════════════ */
    function _buildPipe(ctx, inst) {
        inst.pipeBus = ctx.createGain(); inst.pipeBus.gain.value = 1.0;

        /* tremulant — shallow global amplitude wobble */
        inst.tremGain = ctx.createGain(); inst.tremGain.gain.value = 1.0;
        inst.tremLfo = ctx.createOscillator(); inst.tremLfo.type = 'sine'; inst.tremLfo.frequency.value = 5.0;
        inst.tremDepth = ctx.createGain(); inst.tremDepth.gain.value = 0;
        inst.tremLfo.connect(inst.tremDepth); inst.tremDepth.connect(inst.tremGain.gain);
        inst.tremLfo.start();

        inst.pipeBus.connect(inst.tremGain);
        inst.tremGain.connect(inst.master);
        inst.tremGain.connect(inst.reverbSend);   /* pipe sends generously to cathedral verb */
    }

    function _applyTremulant(inst) {
        if (!inst.tremDepth) return;
        inst.tremDepth.gain.setTargetAtTime(inst.tremulant ? 0.16 : 0, inst.ctx.currentTime, 0.05);
    }

    function _rebuildPipeWave(inst) {
        var N = 18;
        var real = new Float32Array(N + 1);
        var imag = new Float32Array(N + 1);
        var any = false;
        for (var name in inst.stops) {
            if (!inst.stops.hasOwnProperty(name) || !inst.stops[name]) continue;
            if (name === 'bourdon16') continue; /* sub-osc, not part of the 8' wave */
            var spec = STOP_SPECTRA[name];
            if (!spec) continue;
            any = true;
            for (var h = 0; h < spec.length && h < N; h++) {
                imag[h + 1] += spec[h];
            }
        }
        if (!any) { imag[1] = 1; } /* never silent: fall back to a pure 8' */
        /* normalise so summed stops don't blow the limiter */
        var peak = 0;
        for (var i = 1; i <= N; i++) peak += Math.abs(imag[i]);
        if (peak > 0) for (var j = 1; j <= N; j++) imag[j] /= peak;
        inst._pipeWave = inst.ctx.createPeriodicWave(real, imag, { disableNormalization: false });
        inst._pipeWaveDirty = false;
    }

    function _pipeNoteOn(inst, midi) {
        var ctx = inst.ctx, freq = midiToFreq(midi);
        if (inst._pipeWaveDirty || !inst._pipeWave) _rebuildPipeWave(inst);

        var noteGain = ctx.createGain();
        noteGain.gain.value = 0;
        var pan = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
        if (pan) pan.pan.value = Math.max(-0.4, Math.min(0.4, (midi - 66) / 40));

        var oscs = [];
        var main = ctx.createOscillator();
        main.setPeriodicWave(inst._pipeWave);
        main.frequency.value = freq;
        main.connect(noteGain); main.start();
        oscs.push(main);

        /* 16' Bourdon sub-octave */
        if (inst.stops.bourdon16) {
            var sub = ctx.createOscillator(); sub.type = 'triangle';
            sub.frequency.value = freq / 2;
            var subG = ctx.createGain(); subG.gain.value = 0.6;
            sub.connect(subG); subG.connect(noteGain); sub.start();
            oscs.push(sub);
        }

        /* chiff — a brief breathy attack transient for flue stops */
        if (inst.stops.principal8 || inst.stops.flute8 || inst.stops.flute4) {
            _chiff(inst, (pan ? pan : noteGain), freq);
        }

        var dest = pan ? pan : noteGain;
        if (pan) noteGain.connect(pan);
        dest.connect(inst.pipeBus);

        /* soft attack (no decay — pipe holds), gentle level */
        var t = ctx.currentTime, atk = 0.028;
        noteGain.gain.setValueAtTime(0, t);
        noteGain.gain.linearRampToValueAtTime(0.16, t + atk);

        return { kind: 'pipe', midi: midi, oscs: oscs, noteGain: noteGain, pan: pan, releasing: false };
    }

    function _chiff(inst, dest, freq) {
        var ctx = inst.ctx, t = ctx.currentTime;
        var n = ctx.createBufferSource(); n.buffer = _noiseBuf(ctx);
        var bp = ctx.createBiquadFilter(); bp.type = 'bandpass';
        bp.frequency.value = Math.min(7000, freq * 4); bp.Q.value = 0.8;
        var g = ctx.createGain();
        g.gain.setValueAtTime(0.05, t);
        g.gain.exponentialRampToValueAtTime(0.0006, t + 0.05);
        n.connect(bp); bp.connect(g); g.connect(dest);
        n.start(t); n.stop(t + 0.06);
    }

    /* ════════════════════ Hammond note ════════════════════ */
    function _hammondNoteOn(inst, midi, velocity, firstOfPhrase, useBass) {
        var ctx = inst.ctx, t = ctx.currentTime;

        var noteGain = ctx.createGain();
        var noteScale = 0.052;
        var drawGains = [], wheelMidis = [], wired = [];
        var h = { kind: 'hammond', midi: midi, oscs: [], noteGain: noteGain,
                  drawGains: drawGains, wheelMidis: wheelMidis, wired: wired,
                  noteScale: noteScale, isBass: !!useBass, releasing: false };

        for (var i = 0; i < 9; i++) {
            var g = ctx.createGain();
            var amp = _drawbarLevel(inst, i, !!useBass) * noteScale;
            g.gain.value = amp;
            drawGains[i] = g;
            wheelMidis[i] = wheelMidiFor(inst.model, midi, i);
            wired[i] = false;
            if (amp > 0.00001) _wireDrawbar(inst, h, i);   /* zero drawbars stay unwired: no cost */
        }

        /* organ envelope: near-instant on, no decay, short release with a subtle bump */
        noteGain.gain.setValueAtTime(0.0001, t);
        noteGain.gain.exponentialRampToValueAtTime(1.0, t + 0.006);
        noteGain.connect(inst.hammondBus);

        /* key-click — the contact-bounce transient */
        if (inst.click > 0.001) _keyClick(inst, velocity);

        /* percussion — single-trigger, only the first upper key of a phrase */
        if (inst.percussion.on && firstOfPhrase) _percussionHit(inst, midi);

        return h;
    }

    function _keyClick(inst, velocity) {
        var ctx = inst.ctx, t = ctx.currentTime;
        var n = ctx.createBufferSource(); n.buffer = _noiseBuf(ctx);
        var bp = ctx.createBiquadFilter(); bp.type = 'bandpass';
        bp.frequency.value = 2200; bp.Q.value = 0.7;
        var g = ctx.createGain();
        var amp = 0.06 * inst.click * (0.5 + velocity * 0.5);
        g.gain.setValueAtTime(amp, t);
        g.gain.exponentialRampToValueAtTime(0.0004, t + 0.012);
        n.connect(bp); bp.connect(g); g.connect(inst.hammondBus);
        n.start(t); n.stop(t + 0.02);
    }

    /* The percussion is the 2nd (4') and/or 3rd (2⅔') harmonic busbar through a decaying
       amplifier — the same wheels the drawbars read, so it is tempered and, on the spinet,
       just as absent above the top wheel. */
    function _percussionHit(inst, midi) {
        var ctx = inst.ctx, t = ctx.currentTime, p = inst.percussion;
        var spinet = inst.model === 'spinet';
        var harms = (spinet && p.harm === 23) ? [3, 4] : [(p.harm === 2) ? 3 : 4]; /* DRAWBAR_SEMIS index: 4' = 3, 2⅔' = 4 */
        var peak = p.soft ? 0.10 : 0.20;
        var decay = PERC_DECAY[spinet ? 'spinet' : 'console'][p.fast ? 'fast' : 'slow'];
        harms.forEach(function (idx) {
            var wm = wheelMidiFor(inst.model, midi, idx);
            if (wm == null) return;
            var g = ctx.createGain();
            g.gain.setValueAtTime(0.0001, t);
            g.gain.exponentialRampToValueAtTime(peak, t + 0.004);
            /* −60 dB from its own peak: PERCUSSION SOFT is a divider after the decaying
               stage, so it lowers the level without changing how fast it fades */
            g.gain.exponentialRampToValueAtTime(peak * 0.001, t + decay);
            var w = _wheel(inst, wm);
            w.connect(g); g.connect(inst.hammondBus);
            setTimeout(function () { try { w.disconnect(g); g.disconnect(); } catch (e) {} }, (decay + 0.1) * 1000);
        });
    }

    /* ════════════════════ pedal coupler ════════════════════ */
    function _refreshPedal(inst) {
        if (!inst.pedal || inst.heldOrder.length === 0) {
            if (inst._pedalHandle) { _killHandle(inst, inst._pedalHandle); inst._pedalHandle = null; }
            return;
        }
        var lowest = inst.heldOrder.reduce(function (a, b) { return b < a ? b : a; }, 999);
        if (inst._pedalHandle && inst._pedalHandle.midi === lowest) return;
        if (inst._pedalHandle) { _killHandle(inst, inst._pedalHandle); inst._pedalHandle = null; }

        var ctx = inst.ctx, freq = midiToFreq(lowest) / 2, t = ctx.currentTime;
        var bus = (inst.type === 'pipe') ? inst.pipeBus : inst.hammondBus;
        var osc = ctx.createOscillator(); osc.type = 'triangle'; osc.frequency.value = freq;
        var g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.10, t + 0.02);
        osc.connect(g); g.connect(bus); osc.start(t);
        inst._pedalHandle = { kind: 'pedal', midi: lowest, oscs: [osc], noteGain: g, releasing: false };
    }

    /* ════════════════════ release / kill ════════════════════ */
    function _release(inst, midi) {
        var h = inst.activeNotes[midi];
        if (!h || h.releasing) return;
        h.releasing = true;
        var ctx = inst.ctx, t = ctx.currentTime;
        var rel = (h.kind === 'pipe') ? 0.12 : 0.055;  /* pipes decay slower than tonewheels */
        try {
            h.noteGain.gain.cancelScheduledValues(t);
            h.noteGain.gain.setValueAtTime(Math.max(h.noteGain.gain.value, 0.0001), t);
            h.noteGain.gain.exponentialRampToValueAtTime(0.0002, t + rel);
            for (var i = 0; i < h.oscs.length; i++) { try { h.oscs[i].stop(t + rel + 0.04); } catch (e) {} }
        } catch (e) {}
        delete inst.activeNotes[midi];
        var idx = inst.heldOrder.indexOf(midi);
        if (idx >= 0) inst.heldOrder.splice(idx, 1);
        if (inst.pedal) _refreshPedal(inst);
        var ms = (rel + 0.1) * 1000;
        setTimeout(function () { _disconnectHandle(inst, h); }, ms);
    }

    function _killHandle(inst, h) {
        if (!h) return;
        var ctx = inst.ctx, t = ctx ? ctx.currentTime : 0;
        try {
            h.noteGain.gain.cancelScheduledValues(t);
            h.noteGain.gain.setValueAtTime(h.noteGain.gain.value, t);
            h.noteGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.03);
            for (var i = 0; i < h.oscs.length; i++) { try { h.oscs[i].stop(t + 0.04); } catch (e) {} }
        } catch (e) {}
        setTimeout(function () { _disconnectHandle(inst, h); }, 80);
    }

    function _disconnectHandle(inst, h) {
        try {
            for (var i = 0; i < (h.oscs || []).length; i++) h.oscs[i].disconnect();
            if (h.drawGains) for (var j = 0; j < h.drawGains.length; j++) {
                /* unhook this key from the shared wheel — never stop the wheel itself */
                if (h.wired && h.wired[j] && inst._wheels[h.wheelMidis[j]]) { try { inst._wheels[h.wheelMidis[j]].disconnect(h.drawGains[j]); } catch (e) {} }
                try { h.drawGains[j].disconnect(); } catch (e) {}
            }
            if (h.noteGain) h.noteGain.disconnect();
            if (h.pan) h.pan.disconnect();
        } catch (e) {}
    }

    /* ════════════════════ shared DSP utilities ════════════════════ */

    /* Soft-clip (tanh-ish) waveshaper curve. amount 0 = linear. */
    function _softClipCurve(amount) {
        var n = 1024, curve = new Float32Array(n);
        var k = 1 + amount * 18;
        for (var i = 0; i < n; i++) {
            var x = (i / (n - 1)) * 2 - 1;
            curve[i] = Math.tanh(k * x) / Math.tanh(k);
        }
        return curve;
    }

    /* LFO −1…1 → drive 0…1: ((1+x)/2)². Smooth, rests near zero for the negative half of
       the oscillator and rises in a pulse on the positive half, like the driver tube. */
    function _pulseCurve() {
        var n = 1024, curve = new Float32Array(n);
        for (var i = 0; i < n; i++) {
            var u = i / (n - 1);
            curve[i] = u * u;
        }
        return curve;
    }

    /* A small reusable white-noise buffer (0.2 s) for clicks/chiff. */
    var _NOISE = null;
    function _noiseBuf(ctx) {
        if (_NOISE && _NOISE.sampleRate === ctx.sampleRate) return _NOISE;
        var len = Math.floor(ctx.sampleRate * 0.2);
        var b = ctx.createBuffer(1, len, ctx.sampleRate);
        var d = b.getChannelData(0);
        /* deterministic-ish noise (seeded LCG) so offline renders are reproducible */
        var s = 22222;
        for (var i = 0; i < len; i++) { s = (s * 1103515245 + 12345) & 0x7fffffff; d[i] = (s / 0x3fffffff) - 1; }
        _NOISE = b;
        return b;
    }

    /* Stereo decaying-noise impulse response → convolution reverb.
       duration (s), decay shapes the tail; early reflections + progressive HF damping
       give a believable hall/cathedral (same approach as /reverb-echo). */
    function _genIR(ctx, duration, decay) {
        var sr = ctx.sampleRate, len = Math.max(1, Math.floor(sr * duration));
        var ir = ctx.createBuffer(2, len, sr);
        var s = 99991;
        function rnd() { s = (s * 1103515245 + 12345) & 0x7fffffff; return (s / 0x3fffffff) - 1; }
        for (var ch = 0; ch < 2; ch++) {
            var d = ir.getChannelData(ch);
            var lp = 0;
            for (var i = 0; i < len; i++) {
                var t = i / len;
                var env = Math.pow(1 - t, decay);
                var w = rnd() * env;
                /* progressively damp highs toward the tail (air absorption) */
                var a = 0.2 + 0.7 * t;
                lp = lp + a * (w - lp);
                d[i] = lp;
            }
            /* a few discrete early reflections */
            var taps = [0.011, 0.019, 0.031, 0.047, 0.061];
            for (var k = 0; k < taps.length; k++) {
                var pos = Math.floor(taps[k] * sr);
                if (pos < len) d[pos] += (0.5 - k * 0.08) * (ch ? -1 : 1);
            }
        }
        return ir;
    }

    /* Two-spring tank impulse response. Each spring is a delay loop (the round trip) with
       a chain of stretched first-order allpasses in it — their group delay rises toward
       fC, so every echo arrives as a chirp, lows first, and the chirps smear further on
       every trip — plus the spring's high-frequency loss and a loop gain for ~2.4 s decay.
       The transducers pass roughly 120 Hz … 5 kHz. */
    var SPRING_FC = 4300, SPRING_T60 = 2.4;
    var SPRINGS = [{ trip: 0.0331, a: 0.62, stages: 24, mix: [1, 0.8] },
                   { trip: 0.0413, a: 0.58, stages: 28, mix: [0.8, 1] }];
    function _genSpringIR(ctx) {
        var sr = ctx.sampleRate, len = Math.floor(sr * 2.8);
        var ir = ctx.createBuffer(2, len, sr);
        var L = ir.getChannelData(0), R = ir.getChannelData(1);
        var K = Math.max(1, Math.round(sr / (2 * SPRING_FC)));
        var lossA = Math.exp(-2 * Math.PI * SPRING_FC / sr);
        for (var s = 0; s < SPRINGS.length; s++) {
            var sp = SPRINGS[s];
            var D = Math.max(1, Math.round(sp.trip * sr));
            var g = Math.pow(10, -3 * sp.trip / SPRING_T60);
            var line = new Float32Array(D), w = 0;
            var xs = [], ys = [];
            for (var m = 0; m < sp.stages; m++) { xs.push(new Float32Array(K)); ys.push(new Float32Array(K)); }
            var k = 0, lp = 0, a = sp.a;
            for (var n = 0; n < len; n++) {
                var out = line[w];
                var x = (n === 0 ? 1 : 0) + g * out;
                for (var st = 0; st < sp.stages; st++) {
                    /* y[n] = a·x[n] + x[n−K] − a·y[n−K] */
                    var y = a * x + xs[st][k] - a * ys[st][k];
                    xs[st][k] = x; ys[st][k] = y; x = y;
                }
                k = (k + 1) % K;
                lp = (1 - lossA) * x + lossA * lp;
                line[w] = lp; w = (w + 1) % D;
                L[n] += out * sp.mix[0]; R[n] += out * sp.mix[1];
            }
        }
        /* transducer band: two one-pole high-passes at 120 Hz, two low-passes at 5 kHz */
        var hpA = Math.exp(-2 * Math.PI * 120 / sr), lpB = Math.exp(-2 * Math.PI * 5000 / sr);
        [L, R].forEach(function (d) {
            for (var pass = 0; pass < 2; pass++) {
                var px = 0, py = 0, q = 0;
                for (var i = 0; i < d.length; i++) {
                    var hx = d[i];
                    py = hpA * (py + hx - px); px = hx;      /* high-pass */
                    q = (1 - lpB) * py + lpB * q;           /* low-pass */
                    d[i] = q;
                }
            }
        });
        return ir;
    }

    /* ───────────────────────── presets ───────────────────────── */
    var HAMMOND_PRESETS = {
        jazz:      { drawbars: [8,8,8,0,0,0,0,0,0], leslie: 'chorale', vibrato: 'C3', perc: { on: true, harm: 3, soft: true, fast: true }, overdrive: 0 },
        gospel:    { drawbars: [8,8,8,8,0,0,0,8,8], leslie: 'tremolo', vibrato: 'C3', perc: { on: true, harm: 3, soft: false, fast: false }, overdrive: 0.15 },
        rock:      { drawbars: [8,8,8,0,0,0,0,0,8], leslie: 'tremolo', vibrato: 'off', perc: { on: false }, overdrive: 0.55 },
        full:      { drawbars: [8,8,8,8,8,8,8,8,8], leslie: 'tremolo', vibrato: 'off', perc: { on: false }, overdrive: 0.1 },
        ballad:    { drawbars: [8,0,8,4,0,0,0,0,0], leslie: 'chorale', vibrato: 'C2', perc: { on: true, harm: 2, soft: true, fast: false }, overdrive: 0 },
        whiter:    { drawbars: [8,8,8,8,0,0,0,0,0], leslie: 'chorale', vibrato: 'V3', perc: { on: false }, overdrive: 0 }
    };
    /* L-100 / L-122 registrations — what the spinet does that the console cannot: both
       percussion harmonics at once, the 2½-second percussion ring, the phase-shift chorus
       and the spring tank. */
    var SPINET_PRESETS = {
        soul:   { drawbars: [8,8,8,0,0,0,0,0,0], leslie: 'chorale', vibrato: 'CN',  perc: { on: true, harm: 23, soft: false, fast: false }, overdrive: 0.1, reverb: 1 },
        rock:   { drawbars: [8,8,8,8,0,0,0,0,8], leslie: 'tremolo', vibrato: 'off', perc: { on: true, harm: 3, soft: false, fast: true }, overdrive: 0.6, reverb: 0 },
        ballad: { drawbars: [0,0,8,6,0,4,0,0,0], leslie: 'chorale', vibrato: 'SN',  perc: { on: false }, overdrive: 0, reverb: 3 },
        home:   { drawbars: [0,0,8,8,6,4,0,0,0], leslie: 'brake',   vibrato: 'CS',  perc: { on: true, harm: 2, soft: true, fast: false }, overdrive: 0, reverb: 2 }
    };
    var PIPE_PRESETS = {
        flute:      { stops: { bourdon16: false, principal8: false, flute8: true, flute4: false, twelfth: false, fifteenth: false, mixture: false, reed8: false }, tremulant: false, reverb: 0.45 },
        principal:  { stops: { bourdon16: false, principal8: true, flute8: false, flute4: true, twelfth: false, fifteenth: false, mixture: false, reed8: false }, tremulant: false, reverb: 0.5 },
        plenum:     { stops: { bourdon16: true, principal8: true, flute8: false, flute4: true, twelfth: true, fifteenth: true, mixture: true, reed8: false }, tremulant: false, reverb: 0.6 },
        tutti:      { stops: { bourdon16: true, principal8: true, flute8: true, flute4: true, twelfth: true, fifteenth: true, mixture: true, reed8: true }, tremulant: false, reverb: 0.7 },
        voxhumana:  { stops: { bourdon16: false, principal8: false, flute8: true, flute4: false, twelfth: false, fifteenth: false, mixture: false, reed8: true }, tremulant: true, reverb: 0.55 }
    };

    return {
        create: create,
        midiToFreq: midiToFreq,
        foldHarmonic: foldHarmonic,
        wheelMidiFor: wheelMidiFor,
        vibratoFor: vibratoFor,
        drawbarAmp: drawbarAmp,
        DRAWBAR_RATIOS: DRAWBAR_RATIOS,
        DRAWBAR_SEMIS: DRAWBAR_SEMIS,
        STOP_SPECTRA: STOP_SPECTRA,
        LESLIE_SPEED: LESLIE_SPEED,
        LESLIE_TAU: LESLIE_TAU,
        SCANNER_DEPTH: SCANNER_DEPTH,
        PHASE_VIB_SWEEP: PHASE_VIB_SWEEP,
        PERC_DECAY: PERC_DECAY,
        HAMMOND_PRESETS: HAMMOND_PRESETS,
        SPINET_PRESETS: SPINET_PRESETS,
        PIPE_PRESETS: PIPE_PRESETS
    };
})();
